<script setup lang="ts">
// hh.ru-style content-rich home: a search-first hero with popular professions,
// «вакансии дня» as crawlable links, companies, professions, explainer, FAQ.
import { useElementVisibility, useMediaQuery } from '@vueuse/core'
import ListingPreviewLink from '~/components/listing/ListingPreviewLink.vue'
import { Button } from '~/components/ui/button'
import { LANDING_CITY, useLandingShowcase } from '~/composables/landing/use-landing-showcase'
import type { SearchResult } from '~/entities/search/search-state'
import { faq, popularSearches, vacanciesWord } from './landing-content'
import LandingLiveFeed from './LandingLiveFeed.vue'
import LandingMockChat from './LandingMockChat.vue'
import LandingMockResponses from './LandingMockResponses.vue'
import LandingPhone from './LandingPhone.vue'
import LandingTestimonials from './LandingTestimonials.vue'
import LandingVacancyRow from './LandingVacancyRow.vue'
import LandingBeforeAfter from './LandingBeforeAfter.vue'
import LandingScrollStory from './LandingScrollStory.vue'
import LandingVideoCard from './LandingVideoCard.vue'

const { data: showcase } = await useLandingShowcase()

/** Vacancies of the day shown before «Показать ещё». */
const DAY_PREVIEW = 3

/** The public search page: guests see the first results there and sign in for the rest. */
function searchLink(query = '') {
  return { path: '/search', query: { city: LANDING_CITY.alias, source: 'vacancies', ...(query ? { query } : {}) } }
}

const searchText = ref('')
async function submitSearch() {
  await navigateTo(searchLink(searchText.value.trim()))
}

const activeFilter = ref<string | null>(null)
const expanded = ref(false)
const filtered = computed(() => showcase.value.vacancies.filter(vacancy =>
  activeFilter.value === null || vacancy.profession?.title === activeFilter.value))
const visibleVacancies = computed(() => expanded.value ? filtered.value : filtered.value.slice(0, DAY_PREVIEW))

const professions = computed(() => {
  const counts = new Map<string, number>()
  for (const vacancy of showcase.value.vacancies) {
    const title = vacancy.profession?.title
    if (title) counts.set(title, (counts.get(title) ?? 0) + 1)
  }
  return [...counts].sort((a, b) => b[1] - a[1]).map(([title, count]) => ({ title, count }))
})

const companies = computed(() => {
  const byName = new Map<string, { name: string, logo: string | null, vacancy: SearchResult, count: number }>()
  for (const vacancy of showcase.value.vacancies) {
    const name = vacancy.company?.name
    if (!name) continue
    const entry = byName.get(name)
    if (entry) entry.count++
    else byName.set(name, { name, logo: vacancy.company?.logo ?? null, vacancy, count: 1 })
  }
  return [...byName.values()].slice(0, 12)
})

const hero = useTemplateRef<HTMLElement>('hero')
const heroVisible = useElementVisibility(hero)
const isDesktop = useMediaQuery('(min-width: 1024px)')

function pick(value: string) {
  activeFilter.value = activeFilter.value === value ? null : value
  expanded.value = false
  document.getElementById('vacancies-of-the-day')?.scrollIntoView({ behavior: 'smooth' })
}

const explainer = [
  {
    id: 'applicant',
    title: 'Соискателю',
    lead: 'Вместо резюме — минута видео о себе.',
    points: ['Можно без опыта', 'Отклик в один тап, ответ — в чат'],
    cta: 'Найти работу',
    to: '/signup/applicant',
    tone: 'bg-secondary',
    accent: 'text-primary',
    button: '',
  },
  {
    id: 'employer',
    title: 'Работодателю',
    lead: 'Смотрите кандидатов до собеседования.',
    points: ['Видеовакансия — за пару минут', 'Отклики сразу с видео'],
    cta: 'Разместить вакансию',
    to: '/signup/employer',
    tone: 'bg-orange-100',
    accent: 'text-orange-600',
    button: 'bg-neutral-950 text-white hover:bg-neutral-800',
  },
] as const

const allFaq = computed(() => [...faq.applicant, ...faq.employer])

useHead({
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: allFaq.value.map(item => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
    }),
  }],
})
</script>

<template>
  <div class="min-h-screen bg-background pb-24 text-foreground md:pb-28">
    <header class="sticky top-0 z-40 border-b bg-background/90 backdrop-blur-md">
      <div class="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 md:h-16 md:gap-6 md:px-6">
        <Icon name="local:logo-full" class="h-7 w-21 shrink-0 md:h-8 md:w-24" />
        <span class="flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium md:bg-transparent md:px-0 md:text-sm md:text-muted-foreground">
          <Icon name="tabler:map-pin-filled" class="size-3.5 text-primary md:size-4 md:text-muted-foreground" />
          Новосибирск
        </span>
        <nav class="ml-auto hidden items-center gap-5 text-sm font-medium md:flex">
          <a href="#for-applicants" class="hover:text-primary">Соискателям</a>
          <a href="#for-employers" class="hover:text-primary">Работодателям</a>
          <a href="#faq" class="hover:text-primary">Вопросы</a>
        </nav>
        <div class="ml-auto flex items-center gap-2 md:ml-0">
          <Button as-child variant="ghost" size="sm">
            <NuxtLink to="/login">Войти</NuxtLink>
          </Button>
          <Button as-child size="sm" shape="pill" class="hidden px-4 md:inline-flex">
            <NuxtLink to="/signup">Создать видеорезюме</NuxtLink>
          </Button>
        </div>
      </div>
    </header>

    <section ref="hero" class="bg-linear-to-b from-hero-background/70 to-background">
      <div class="mx-auto grid max-w-6xl items-center gap-8 px-4 pb-6 pt-6 md:px-6 lg:grid-cols-[1fr_auto] lg:gap-10 lg:py-14">
        <div class="landing-hero-out min-w-0">
          <h1 class="text-[2rem] font-bold leading-[1.1] tracking-tight sm:text-5xl">
            Работа {{ LANDING_CITY.titleIn }} по&nbsp;видео
          </h1>
          <p class="mt-3 text-base text-muted-foreground sm:max-w-xl sm:text-lg">
            Вакансии в кафе, ресторанах и магазинах — без резюме.
          </p>
          <form role="search" class="mt-5 flex h-14 max-w-xl items-center gap-2 rounded-full bg-background pl-4 pr-1.5 shadow-sm ring-1 ring-border focus-within:ring-2 focus-within:ring-primary sm:mt-7" @submit.prevent="submitSearch">
            <Icon name="tabler:search" class="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <input
              v-model="searchText"
              type="search"
              enterkeyhint="search"
              aria-label="Профессия или компания"
              placeholder="Профессия или компания"
              class="h-full min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
            >
            <Button type="submit" shape="pill" class="h-11 shrink-0 px-5 text-base">Найти</Button>
          </form>
          <nav aria-label="Популярные профессии" class="mt-4 flex flex-wrap gap-2">
            <NuxtLink
              v-for="tag in popularSearches"
              :key="tag"
              :to="searchLink(tag)"
              class="flex h-9 shrink-0 items-center rounded-full bg-background px-3.5 text-sm font-medium ring-1 ring-border transition-colors hover:text-primary hover:ring-primary sm:h-10 sm:px-4"
            >
              {{ tag }}
            </NuxtLink>
            <NuxtLink
              :to="searchLink()"
              class="flex h-9 shrink-0 items-center gap-1 rounded-full px-3.5 text-sm font-medium text-primary transition-colors hover:bg-secondary sm:h-10 sm:px-4"
            >
              Все вакансии
              <Icon name="tabler:arrow-right" class="size-4" aria-hidden="true" />
            </NuxtLink>
          </nav>
        </div>
        <div v-if="isDesktop" class="w-72">
          <LandingPhone>
            <LandingLiveFeed :items="showcase.feed" />
          </LandingPhone>
        </div>
      </div>
    </section>

    <section v-if="showcase.feed.length" class="mx-auto max-w-6xl px-4 pt-6 md:px-6 md:pt-12">
      <h2 class="text-2xl font-bold leading-tight sm:text-3xl lg:text-5xl">Вакансии, которые можно посмотреть</h2>
      <p class="mt-2 text-muted-foreground sm:text-lg">Работодатели сами показывают зал, команду и смены. Нажми на карточку.</p>
      <div class="mt-6 grid gap-5 pb-4 lg:grid-cols-3 lg:gap-6">
        <ListingPreviewLink
          v-for="(item, index) in showcase.feed.slice(0, 3)"
          :key="item.id"
          kind="vacancy"
          :id="item.id"
          class="sticky block lg:static"
          :style="{ top: `${4.5 + index * 1}rem` }"
        >
          <LandingVideoCard :item="item" />
        </ListingPreviewLink>
      </div>
    </section>

    <section class="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-16">
      <h2 class="text-2xl font-bold leading-tight sm:text-3xl lg:text-5xl">Резюме или видео?</h2>
      <p class="mt-2 text-muted-foreground sm:text-lg">Двигай шторку и сравни.</p>
      <LandingBeforeAfter class="mt-6" />
    </section>


    <section class="mx-auto grid max-w-6xl gap-3 px-4 py-8 md:grid-cols-2 md:gap-4 md:px-6 md:py-12">
      <div
        v-for="block in explainer"
        :id="block.id === 'applicant' ? 'for-applicants' : 'for-employers'"
        :key="block.id"
        class="scroll-reveal relative flex scroll-mt-16 flex-col overflow-hidden rounded-3xl p-5 md:min-h-80 md:p-7"
        :class="block.tone"
      >
        <div class="relative z-10 md:max-w-[60%]">
          <h2 class="text-2xl font-bold" :class="block.accent">{{ block.title }}</h2>
          <p class="mt-2 font-medium">{{ block.lead }}</p>
          <ul class="mt-4 space-y-2 text-sm">
            <li v-for="point in block.points" :key="point" class="flex gap-2">
              <Icon name="tabler:check" class="mt-0.5 size-4 shrink-0" :class="block.accent" />
              {{ point }}
            </li>
          </ul>
          <Button as-child shape="pill" class="mt-5 w-full px-5 md:mt-6 md:w-auto" :class="block.button">
            <NuxtLink :to="block.to">{{ block.cta }}</NuxtLink>
          </Button>
        </div>
        <div class="relative -mb-5 mt-5 h-52 overflow-hidden md:absolute md:-bottom-24 md:-right-4 md:m-0 md:h-auto md:w-48 md:rotate-6 md:overflow-visible">
          <div class="mx-auto w-56 md:w-full">
            <LandingPhone>
              <LandingMockChat v-if="block.id === 'applicant'" />
              <LandingMockResponses v-else />
            </LandingPhone>
          </div>
        </div>
      </div>
    </section>

    <section id="vacancies-of-the-day" class="mx-auto max-w-6xl scroll-mt-16 px-4 py-6 md:px-6 md:py-8">
      <div class="flex flex-wrap items-end justify-between gap-2">
        <h2 class="text-xl font-bold sm:text-3xl">Вакансии дня {{ LANDING_CITY.titleIn }}</h2>
        <button v-if="activeFilter" type="button" class="text-sm font-medium text-primary" @click="activeFilter = null">
          Сбросить фильтр
        </button>
      </div>
      <div class="mt-4 grid gap-3 md:mt-6 lg:grid-cols-2">
        <LandingVacancyRow v-for="vacancy in visibleVacancies" :key="vacancy.id" :item="vacancy" class="scroll-reveal" />
      </div>
      <p v-if="!visibleVacancies.length" class="mt-6 text-muted-foreground">Среди свежих вакансий таких пока нет.</p>
      <div class="mt-4 flex flex-col gap-2 sm:mt-6 sm:flex-row sm:gap-3">
        <Button v-if="!expanded && filtered.length > visibleVacancies.length" variant="outline" shape="pill" class="px-6" @click="expanded = true">
          Показать ещё {{ filtered.length - visibleVacancies.length }}
        </Button>
        <Button as-child shape="pill" variant="secondary" class="px-6">
          <NuxtLink :to="searchLink()">Все {{ showcase.total }} {{ vacanciesWord(showcase.total) }}</NuxtLink>
        </Button>
      </div>
    </section>

    <section class="py-6 md:py-10">
      <h2 class="mx-auto max-w-6xl px-4 text-xl font-bold sm:text-3xl md:px-6">Работа в компаниях {{ LANDING_CITY.titleOf }}</h2>
      <div class="mx-auto mt-4 grid max-w-6xl grid-cols-2 gap-3 px-4 md:mt-6 md:grid-cols-3 md:px-6 lg:grid-cols-4">
        <ListingPreviewLink
          v-for="company in companies.slice(0, 8)"
          :key="company.name"
          kind="vacancy"
          :id="company.vacancy.id"
          class="scroll-reveal flex h-full flex-col items-start gap-2 rounded-2xl border border-border/70 p-3 transition-shadow hover:shadow-md md:flex-row md:items-center md:gap-3"
        >
          <img v-if="company.logo" :src="company.logo" :alt="company.name" loading="lazy" class="size-12 shrink-0 rounded-xl object-cover">
          <span class="w-full min-w-0">
            <span class="line-clamp-2 text-sm font-semibold leading-tight md:line-clamp-1 md:text-base">{{ company.name }}</span>
            <span class="mt-0.5 block text-xs text-muted-foreground md:text-sm">{{ company.count }} {{ vacanciesWord(company.count) }}</span>
          </span>
        </ListingPreviewLink>
      </div>
    </section>

    <section class="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
      <h2 class="text-xl font-bold sm:text-3xl">Работа по профессиям {{ LANDING_CITY.titleIn }}</h2>
      <div class="mt-4 flex flex-wrap gap-2 md:mt-6">
        <button
          v-for="profession in professions"
          :key="profession.title"
          type="button"
          class="flex h-10 items-center gap-2 rounded-xl px-3.5 text-sm font-medium transition-colors md:h-11 md:px-4 md:text-base"
          :class="activeFilter === profession.title ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-secondary hover:text-primary'"
          @click="pick(profession.title)"
        >
          {{ profession.title }}
          <span class="text-xs opacity-60 md:text-sm">{{ profession.count }}</span>
        </button>
      </div>
    </section>

    <section class="mt-4 bg-muted/50 pt-10 md:mt-6 md:pt-16">
      <div class="mx-auto max-w-6xl px-4 md:px-6">
        <h2 class="text-xl font-bold sm:text-3xl lg:text-5xl">Как работает Sobes</h2>
        <p class="mt-1 text-sm text-muted-foreground sm:text-base">Три шага для соискателя и три — для работодателя. Просто листай вниз.</p>
      </div>
      <LandingScrollStory :feed="showcase.feed" />
    </section>

    <section class="py-10 md:py-16">
      <div class="mx-auto max-w-6xl px-4 md:px-6">
        <h2 class="text-xl font-bold sm:text-3xl">Отзывы о Sobes</h2>
        <p class="mt-1 text-sm text-muted-foreground md:text-base">Реальные истории — как видео помогло получить работу</p>
      </div>
      <LandingTestimonials class="mx-auto mt-4 max-w-6xl px-4 md:mt-6 md:px-6" />
    </section>

    <section id="faq" class="mx-auto max-w-6xl scroll-mt-16 px-4 py-6 md:px-6 md:py-10">
      <h2 class="text-xl font-bold sm:text-3xl">Вопросы и ответы</h2>
      <div class="mt-4 grid gap-6 md:mt-6 md:grid-cols-2 md:gap-8">
        <div v-for="block in explainer" :key="block.id">
          <h3 class="font-semibold md:text-lg" :class="block.accent">{{ block.title }}</h3>
          <div class="mt-3 divide-y rounded-2xl border">
            <details v-for="item in faq[block.id]" :key="item.q" class="group p-4">
              <summary class="flex cursor-pointer list-none items-center justify-between gap-3 font-medium">
                {{ item.q }}
                <Icon name="tabler:chevron-down" class="size-4 shrink-0 transition-transform group-open:rotate-180" />
              </summary>
              <p class="mt-2 text-sm text-muted-foreground">{{ item.a }}</p>
            </details>
          </div>
        </div>
      </div>
    </section>

    <section class="mx-auto max-w-6xl px-4 py-6 text-sm leading-relaxed text-muted-foreground md:px-6 md:py-10">
      <h2 class="font-semibold text-foreground">Работа {{ LANDING_CITY.titleIn }} без резюме</h2>
        <p class="mt-2 max-w-4xl">
          Sobes — сервис поиска работы и сотрудников в формате коротких видео. Соискатели записывают видеорезюме и откликаются на вакансии бариста, официантов, поваров, кассиров, продавцов и администраторов. Работодатели — кофейни, рестораны, сети быстрого питания и магазины — публикуют видеовакансии и видят кандидатов ещё до собеседования. Подходит для подработки, работы без опыта, для студентов и сменного графика.
        </p>
    </section>

    <footer class="border-t">
      <div class="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-4 py-8 text-sm md:grid-cols-4 md:gap-8 md:px-6 md:py-10">
        <div class="col-span-2 md:col-span-1">
          <Icon name="local:logo-full" class="h-8 w-24" />
          <p class="mt-2 text-muted-foreground">Работа и сотрудники по видео</p>
        </div>
        <div>
          <p class="font-semibold">Соискателям</p>
          <ul class="mt-3 space-y-2 text-muted-foreground">
            <li><NuxtLink to="/signup/applicant" class="hover:text-foreground">Создать видеорезюме</NuxtLink></li>
            <li><a href="#vacancies-of-the-day" class="hover:text-foreground">Вакансии дня</a></li>
            <li><NuxtLink to="/login" class="hover:text-foreground">Войти</NuxtLink></li>
          </ul>
        </div>
        <div>
          <p class="font-semibold">Работодателям</p>
          <ul class="mt-3 space-y-2 text-muted-foreground">
            <li><NuxtLink to="/signup/employer" class="hover:text-foreground">Разместить вакансию</NuxtLink></li>
            <li><a href="#for-employers" class="hover:text-foreground">Как это работает</a></li>
          </ul>
        </div>
        <div>
          <p class="font-semibold">Sobes</p>
          <ul class="mt-3 space-y-2 text-muted-foreground">
            <li><a href="#" class="hover:text-foreground">О сервисе</a></li>
            <li><a href="#" class="hover:text-foreground">Помощь</a></li>
            <li><a href="#" class="hover:text-foreground">Пользовательское соглашение</a></li>
          </ul>
        </div>
      </div>
    </footer>

    <Transition enter-from-class="translate-y-full" leave-to-class="translate-y-full" enter-active-class="transition-transform duration-300" leave-active-class="transition-transform duration-300">
      <div v-if="!heroVisible" class="fixed inset-x-0 bottom-0 z-40 grid grid-cols-[1fr_auto] gap-2 border-t bg-background/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-md md:hidden">
        <Button as-child shape="pill" class="h-12 text-base">
          <NuxtLink to="/signup/applicant">Найти работу</NuxtLink>
        </Button>
        <Button as-child shape="pill" variant="outline" class="h-12 px-4">
          <NuxtLink to="/signup/employer">Я работодатель</NuxtLink>
        </Button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
@keyframes landing-hero-out {
  to { opacity: 0.2; transform: translateY(-3rem) scale(0.96); }
}

@supports (animation-timeline: view()) {
  @media (prefers-reduced-motion: no-preference) {
    .landing-hero-out {
      animation: landing-hero-out linear both;
      animation-timeline: view();
      animation-range: exit 0% exit 80%;
    }
  }
}
</style>
